import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import PillSearchField from "../../../components/PillSearchField";
import { fetchCustomers } from "../../../Services/customerAccessService";

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function capitalize(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Customers tab body - a DataGrid list (matching Orders.js's table, per the
// explicit "show like order page" request) rather than the master-detail
// two-pane pattern the Therapists tab uses next to it.
export default function CustomersTab() {
  const navigate = useNavigate();
  const [customers, setCustomers] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 30 });

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    let cancelled = false;
    const { page, pageSize } = paginationModel;
    setLoading(true);
    setError(null);

    fetchCustomers({ search: debouncedSearch, page: page + 1, limit: pageSize })
      .then((res) => {
        if (cancelled) return;
        const items = res?.data?.customers || [];
        setCustomers(items);
        setTotalCount(res?.data?.total ?? items.length);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load customers.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, paginationModel.page, paginationModel.pageSize]);

  const columns = [
    { field: "email", headerName: "Email", minWidth: 220, flex: 2 },
    {
      field: "name",
      headerName: "Name",
      minWidth: 160,
      flex: 1.5,
      valueGetter: (value, row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "-",
    },
    { field: "phone", headerName: "Phone", minWidth: 140, flex: 1, valueGetter: (value, row) => row.phone || "-" },
    {
      field: "state",
      headerName: "State",
      minWidth: 100,
      flex: 0.8,
      valueGetter: (value, row) => capitalize(row.state) || "-",
    },
    {
      field: "created_at",
      headerName: "Joined",
      minWidth: 120,
      flex: 1,
      valueGetter: (value, row) => formatDate(row.created_at),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <PillSearchField
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search name, email, phone…"
          resultCount={totalCount}
          loading={loading}
        />
      </Box>

      <Box sx={{ backgroundColor: "#FFFFFF", border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        {error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <DataGrid
            rows={customers}
            columns={columns}
            loading={loading}
            autoHeight
            rowHeight={44}
            columnHeaderHeight={40}
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[30, 50, 100]}
            disableColumnSelector
            disableDensitySelector
            onRowClick={(params) =>
              navigate(`/portal-users/customers/${params.row.id}`, { state: { customer: params.row } })
            }
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none !important" },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none !important" },
            }}
          />
        )}
      </Box>
    </Box>
  );
}
